export class ImageCapture {
  private modal: HTMLDivElement | null = null;
  private stream: MediaStream | null = null;
  private video: HTMLVideoElement | null = null;

  /**
   * Capture a photo with live camera preview
   * Shows modal with camera feed, allows user to take photo or cancel
   * Compresses image to HD size (max 1920x1080) as JPEG
   * @returns Promise<File> - Compressed JPEG file or null if cancelled
   */
  async capture(): Promise<File | null> {
    return new Promise(async (resolve, reject) => {
      try {
        // Request camera access
        this.stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment', // Use back camera on mobile
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          } 
        });

        // Create modal for camera preview
        this.modal = document.createElement('div');
        this.modal.className = 'image-capture-modal';
        this.modal.innerHTML = `
          <div class="image-capture-content">
            <div class="image-capture-header">
              <h3>Take Photo</h3>
              <button class="image-capture-close" id="cancelCapture">✕</button>
            </div>
            <div class="image-capture-preview">
              <video id="cameraPreview" autoplay playsinline></video>
            </div>
            <div class="image-capture-controls">
              <button class="btn btn-secondary" id="cancelCaptureBtn">Cancel</button>
              <button class="btn btn-primary" id="takePictureBtn">📷 Capture Photo</button>
            </div>
          </div>
        `;

        document.body.appendChild(this.modal);

        // Get video element and start preview
        this.video = document.getElementById('cameraPreview') as HTMLVideoElement;
        if (this.video) {
          this.video.srcObject = this.stream;
          await this.video.play();
        }

        // Handle capture button
        const captureBtn = document.getElementById('takePictureBtn');
        if (captureBtn) {
          captureBtn.addEventListener('click', async () => {
            const file = await this.captureFrame();
            this.cleanup();
            resolve(file);
          });
        }

        // Handle cancel buttons
        const cancelBtn = document.getElementById('cancelCaptureBtn');
        const closeBtn = document.getElementById('cancelCapture');
        
        const cancelHandler = () => {
          this.cleanup();
          resolve(null);
        };

        if (cancelBtn) cancelBtn.addEventListener('click', cancelHandler);
        if (closeBtn) closeBtn.addEventListener('click', cancelHandler);

      } catch (error) {
        console.error('Camera access error:', error);
        this.cleanup();
        reject(error);
      }
    });
  }

  /**
   * Capture current frame from video and compress to HD JPEG
   * @returns Promise<File> - Compressed JPEG file
   */
  private async captureFrame(): Promise<File> {
    return new Promise((resolve) => {
      if (!this.video) {
        throw new Error('Video element not available');
      }

      const canvas = document.createElement('canvas');
      const video = this.video;

      // Get video dimensions
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      // Calculate HD size (max 1920x1080) while maintaining aspect ratio
      const maxWidth = 1920;
      const maxHeight = 1080;
      let targetWidth = videoWidth;
      let targetHeight = videoHeight;

      if (videoWidth > maxWidth || videoHeight > maxHeight) {
        const aspectRatio = videoWidth / videoHeight;
        
        if (aspectRatio > maxWidth / maxHeight) {
          // Width is the limiting factor
          targetWidth = maxWidth;
          targetHeight = Math.round(maxWidth / aspectRatio);
        } else {
          // Height is the limiting factor
          targetHeight = maxHeight;
          targetWidth = Math.round(maxHeight * aspectRatio);
        }
      }

      // Set canvas to target dimensions
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // Draw video frame to canvas
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
      }

      // Convert to compressed JPEG blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const timestamp = new Date().getTime();
            const file = new File([blob], `photo_${timestamp}.jpg`, {
              type: 'image/jpeg',
              lastModified: timestamp
            });
            resolve(file);
          }
        },
        'image/jpeg',
        0.85 // 85% quality for good balance between size and quality
      );
    });
  }

  /**
   * Clean up camera resources and remove modal
   */
  private cleanup() {
    // Stop camera stream
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    // Remove modal from DOM
    if (this.modal) {
      document.body.removeChild(this.modal);
      this.modal = null;
    }

    this.video = null;
  }
}
